import React from 'react';
import PropTypes from 'prop-types';
import TruncatedText from '../TruncatedText';
import styles from './ModalParts.module.scss';

/* ────────────────────────────────────────────────────────────
 * ModalTitle — the title row that goes in <Modal title={...}>.
 *
 * Centralizes the editor-modal title contract documented in
 * PROMPT_TEMPLATE.md §4 d2 so every editor modal across every
 * module shares the same shape: a small gradient-tile icon, a
 * heading, and a one-line subtitle.
 *
 *   <Modal
 *     title={
 *       <ModalTitle
 *         icon={<Sparkles size={18} strokeWidth={1.75} />}
 *         heading="Edit menu version"
 *         subtitle="Update the name, source menu, or active schedule."
 *       />
 *     }
 *     ...
 *   />
 *
 * `tone` swaps the icon tile's background:
 *   - 'primary' (default) — brand gradient. For neutral edits.
 *   - 'success' / 'warning' / 'danger' / 'info' — soft-state
 *     tints. Reserved for confirmation modals matching the tone
 *     of the action being confirmed (e.g. tone='danger' on a
 *     delete confirmation).
 * ──────────────────────────────────────────────────────────── */

const TONE_MAP = {
  primary: styles.toneIconPrimary,
  success: styles.toneIconSuccess,
  warning: styles.toneIconWarning,
  danger: styles.toneIconDanger,
  info: styles.toneIconInfo,
};

const ModalTitle = ({ icon, heading, subtitle, tone }) => (
  <span className={styles.title}>
    {icon && (
      <span
        className={[styles.titleIcon, TONE_MAP[tone] || TONE_MAP.primary]
          .filter(Boolean)
          .join(' ')}
        aria-hidden="true"
      >
        {icon}
      </span>
    )}
    <span className={styles.titleText}>
      {/* A string heading goes through TruncatedText so the full value
          is still reachable on hover once the clamp cuts it — headings
          built from user data (document names, file names) are
          routinely longer than the dialog is wide. */}
      {typeof heading === 'string' || typeof heading === 'number' ? (
        <TruncatedText className={styles.titleHeading} text={heading} />
      ) : (
        <span className={styles.titleHeading}>{heading}</span>
      )}
      {subtitle && <span className={styles.titleSub}>{subtitle}</span>}
    </span>
  </span>
);

ModalTitle.propTypes = {
  icon: PropTypes.node,
  heading: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  tone: PropTypes.oneOf(['primary', 'success', 'warning', 'danger', 'info']),
};

ModalTitle.defaultProps = {
  icon: null,
  subtitle: null,
  tone: 'primary',
};

export default ModalTitle;
